import { isTimeoutError, withRequestDeadline } from "../lib/requestDeadline";
import type { UploadProgress } from "../lib/uploadWithProgress";
import { formatVideoDuration } from "../lib/formatVideoDuration";
import { reportOnboardingProgress, reportOnboardingFailure } from "../lib/onboardingTelemetry";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  Upload,
  Scan,
  Check,
  ChevronLeft,
  FileText,
  Globe,
  ImagePlus,
  LayoutTemplate,
  Link as LinkIcon,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useWorkspaceConnections } from "../hooks/useWorkspaceConnections";
import { useFirebaseUrl } from "../hooks/useFirebaseUrl";
import { logoSurfaceClass, useLogoTone } from "../lib/logoTone";
import {
  BusinessDraft,
  normalizeWebsite,
  getBusinessWebsiteGuidance,
  instagramHandle,
  onboardingRequest,
  OnboardingState,
} from "../lib/onboarding";
import {
  type BrandKitExtraction,
  commitBrandKitAssets,
  extractBrandKitFromUrl,
  recoverBrandKitScan,
  uploadBrandKitVisualAsset,
  uploadBrandKitAsset,
} from "../lib/brandKitUtils";
import { connectionMayHaveDropped, isConnectionDrop, retryAfterReconnect, watchVisibility } from "../lib/whenReachable";
import { creationIntent, creationDestination, onboardingReturnPath, readCreationIntent, rememberCreationIntent, readSourceCheck, rememberSourceCheck } from "../lib/onboardingIntent";
import { BrandAsset } from "../models/BrandKit";
import { getBrandKitAssetUploadErrorMessage } from "../lib/brandKitSaveErrors";
import { Button } from "../components/ui/button";
import { SourceCard, displayLink } from "../components/SourceCard";
import { cn } from "../lib/utils";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

import { IncomingContentCheck, IncomingCheckResult, needsIncomingContentCheck } from "../components/onboarding/IncomingContentCheck";
import { IdeasScanProgress, IdeasScanStatus } from "../components/onboarding/IdeasScanProgress";
import { FirstPostStep } from "../components/onboarding/FirstPostStep";
import { UploadProgressBar } from "../components/onboarding/UploadProgressBar";
import GeneratePage from "./GeneratePage";
import { BrandScanPanel, SetupNotice, SetupProgress, ReviewGroup } from "../components/onboarding/OnboardingChrome";
import { BrandPaletteEditor } from "../components/BrandPaletteEditor";
import { LogoResizeDialog } from "../components/LogoResizeDialog";

const autosaveError =
  "We couldn’t save your latest edits. They’re still here, and Continue will try again.";
const importedDraftSaveError = "Your brand is ready, but we couldn’t save it yet. Your details are still here, and Continue will try again.";
const manualDraftSaveError = "Your description is ready, but we couldn’t save it yet. Your edits are still here, and Continue will try again.";
// Partial successes: nothing is blocked, so they read as notes, not errors.
const imagesImportNotice = "We couldn’t bring in some images from your site. You can upload your own here, or later in Brand Kits.";
const audiencesNotice = "We couldn’t suggest audiences this time. You can add your own, or continue without them.";
const infoNotices = new Set([imagesImportNotice, audiencesNotice]);
const pill = "h-11 rounded-md px-6 font-semibold";
const draftSaveErrors = new Set([autosaveError, importedDraftSaveError, manualDraftSaveError]);
const clearDraftSaveError = (current: string) => draftSaveErrors.has(current) ? "" : current;

export default function OnboardingPage() {
  const { activeWorkspaceId, activeWorkspace } = useAuth();
  if (activeWorkspace && activeWorkspace.role !== "owner")
    return <Navigate to="/generate" replace />;
  return <OnboardingFlow key={activeWorkspaceId} />;
}

function OnboardingFlow() {
  const { currentUser, activeWorkspaceId, refreshCustomerData } = useAuth();
  // Accounts are connected when publishing; the business first-post step
  // still preselects any the workspace already has.
  const { connections } = useWorkspaceConnections();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const businessWebsite = params.get("businessWebsite");
  const [contentIntent, setContentIntent] = useState(() => businessWebsite ? null : creationIntent(params.toString()) ||
    (!params.has("returnTo") && currentUser && activeWorkspaceId ? readCreationIntent(currentUser.uid, activeWorkspaceId) : null));

  const [state, setState] = useState<OnboardingState | null>(null);
  // Read by the source-check callback without restarting a check in flight.
  const stateRef = useRef(state);
  stateRef.current = state;
  const incomingReturn = onboardingReturnPath(params.get("returnTo")) || (contentIntent ? creationDestination(contentIntent) : null);
  const restoredContent = businessWebsite ? null : contentIntent || (state?.returnPath?.startsWith("/generate?") ? creationIntent(state.returnPath.split("?")[1]) : null);
  // Once setup state loads, the server's mode decides. URL hints only cover the
  // first frame, so the two can never disagree about which steps apply.
  const contextual = state?.mode ? state.mode === "create" : !businessWebsite && !!restoredContent;
  const destination = incomingReturn || state?.returnPath || creationDestination(restoredContent);
  const [checkedContent, setCheckedContent] = useState<{ intent: string; result: IncomingCheckResult } | null>(null);
  const storedCheck = useMemo(
    () => restoredContent && currentUser && activeWorkspaceId ? readSourceCheck(currentUser.uid, activeWorkspaceId, restoredContent) : null,
    [restoredContent, currentUser, activeWorkspaceId],
  );
  const sourceCheck = restoredContent
    ? checkedContent?.intent === restoredContent ? checkedContent.result : storedCheck
    : null;
  const needsContentCheck = !!restoredContent && needsIncomingContentCheck(restoredContent) && !sourceCheck;
  // An Instagram profile from the homepage names their business: its website
  // prefills brand setup, or without one, brand setup asks for a website. Its
  // bio and captions make the first post only when no website was scanned.
  const profileOnly = sourceCheck?.reason === "instagram-profile";
  const profileNotice = profileOnly && !sourceCheck?.businessWebsite ? sourceCheck?.message || "" : "";
  // The handle they gave us on the homepage, kept on their brand whatever it's built from.
  const heroHandle = profileOnly ? instagramHandle(new URLSearchParams(restoredContent || "").get("articleUrl") || "") : null;
  const checkIncoming = useCallback((input: Record<string, string>, signal: AbortSignal) => {
    if (!currentUser || !activeWorkspaceId) throw new Error("Workspace unavailable");
    return onboardingRequest(currentUser, activeWorkspaceId, "/content-check", input, "POST", signal);
  }, [currentUser, activeWorkspaceId]);
  const replaceIncoming = (intent: string) => {
    setContentIntent(intent);
    setCheckedContent(null);
    // Keep reload and the eventual completion destination on the replacement.
    setParams(previous => {
      const next = new URLSearchParams(previous);
      for (const key of new URLSearchParams(restoredContent || "").keys()) next.delete(key);
      new URLSearchParams(intent).forEach((value, key) => next.set(key, value));
      next.delete("returnTo");
      return next;
    }, { replace: true });
  };
  const [draft, setDraft] = useState<BusinessDraft | null>(null);
  const [view, setView] = useState<"website" | "manual" | "review">("website");
  const [url, setUrl] = useState("");
  const websiteInput = useRef<HTMLInputElement>(null);
  const [showManualWebsite, setShowManualWebsite] = useState(false);
  const [websiteError, setWebsiteError] = useState("");
  const [description, setDescription] = useState("");
  const [audience, setAudience] = useState("");
  const [assets, setAssets] = useState<BrandAsset[]>([]);
  const [imageUploadError, setImageUploadError] = useState("");
  const [imageSaveFailed, setImageSaveFailed] = useState(false);
  const [failedImages, setFailedImages] = useState<Array<{ file: File; message: string; retryable: boolean }>>([]);
  const imageInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState("");
  // Posts are already being made once the first-post composer submits.
  const [firstPostStarted, setFirstPostStarted] = useState(false);
  const [error, setError] = useState("");
  const [preparingDescription, setPreparingDescription] = useState(false);
  const [scanStage, setScanStage] = useState<string | null>(null);
  const [assetSaveFailed, setAssetSaveFailed] = useState(false);
  const extractionController = useRef<AbortController | null>(null);
  const savedBrand = useRef("");
  const savedAssets = useRef("");
  const [crawlFailed, setCrawlFailed] = useState(false);
  const [sourceUrl, setSourceUrl] = useState("");
  const [sourceOwned, setSourceOwned] = useState(true);
  const [addingSource, setAddingSource] = useState(false);
  const [ideasReady, setIdeasReady] = useState(false);
  const [ideasProgress, setIdeasProgress] = useState<IdeasScanStatus | null>(null);
  const [ideasDelayed, setIdeasDelayed] = useState(false);
  const [deferredIdeas, setDeferredIdeas] = useState(false);
  const discoveryAttempt = useRef(0);
  const logoInput = useRef<HTMLInputElement>(null);
  const [resizingLogo, setResizingLogo] = useState(false);
  // Logo problems belong beside the logo, not in the page-level slot by Continue.
  const [logoUploadError, setLogoUploadError] = useState("");
  const [uploadProgress, setUploadProgress] = useState<{ target: "logo" | "images"; percent: number; label?: string } | null>(null);
  const [manualMissing, setManualMissing] = useState({ name: false, description: false });
  const initialScan = useRef(false);
  const leftSavedSocials = useRef(false);
  const alive = useRef(true);
  const extractionAttempt = useRef(0);
  const heading = useRef<HTMLHeadingElement>(null);
  const errorNode = useRef<HTMLDivElement>(null);
  const writes = useRef<Promise<unknown>>(Promise.resolve());
  // Compare against the submitted draft, not the server's trimmed/normalized copy.
  const lastSavedDraft = useRef("");
  const { url: logoUrl, error: logoError } = useFirebaseUrl(draft?.logoPath);
  const logoTone = useLogoTone(logoUrl);
  // Setup finished in another tab leaves this one showing steps the server
  // refuses; follow it out instead of leaving a dead "no longer active" error.
  const leaveFinishedSetup = useRef<(returnPath?: string) => void>(() => undefined);
  leaveFinishedSetup.current = (returnPath) =>
    navigate(incomingReturn || returnPath || destination, { replace: true });
  const api = useCallback(
    (path = "", body?: unknown, method?: string, signal?: AbortSignal) => {
      if (!currentUser || !activeWorkspaceId)
        throw new Error("Workspace is unavailable.");
      const send = () =>
        (signal
          ? onboardingRequest(currentUser, activeWorkspaceId, path, body, method, signal)
          : onboardingRequest(currentUser, activeWorkspaceId, path, body, method))
          .catch((err) => {
            if (err?.code === "SETUP_COMPLETE" && alive.current) leaveFinishedSetup.current(err.returnPath);
            throw err;
          });
      if (signal || body === undefined || path === "/describe") return send();
      const request = writes.current
        .catch(() => undefined)
        .then(() => {
          if (!alive.current) throw new Error("Workspace changed.");
          return send();
        });
      writes.current = request;
      return request;
    },
    [currentUser, activeWorkspaceId],
  );
  const resolveIncoming = useCallback((result: IncomingCheckResult) => {
    if (!restoredContent) return;
    setCheckedContent({ intent: restoredContent, result });
    if (currentUser && activeWorkspaceId) rememberSourceCheck(currentUser.uid, activeWorkspaceId, restoredContent, result);
    // A business site pasted on the homepage stays the first post's source,
    // and its site also prefills brand setup (the server never replaces a
    // website the visitor already entered).
    const current = stateRef.current;
    if (result.status !== "readable" || !result.businessWebsite || !current || current.step !== "business" ||
        current.brandSaved || current.draft?.website || new URLSearchParams(restoredContent).get("source") !== "homepage") return;
    api("/start", { mode: "create", businessWebsite: result.businessWebsite })
      .then(({ state: saved }: { state: OnboardingState }) => {
        const website = saved?.draft?.website;
        if (!alive.current || !website) return;
        setState(saved);
        setDraft((prev) => (prev && !prev.website ? { ...prev, website } : prev));
        setUrl((typed) => typed || website);
      })
      .catch(() => { /* The suggestion is optional; setup continues without it. */ });
  }, [restoredContent, currentUser, activeWorkspaceId, api]);

  // Only while they still need a website: not once they've entered or scanned one.
  const showProfileNotice = !!profileNotice && view === "website" && !state?.brandSaved && !draft?.website;
  useEffect(() => {
    if (showProfileNotice) setWebsiteError((current) => current || profileNotice);
  }, [showProfileNotice, profileNotice]);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      extractionAttempt.current++;
      extractionController.current?.abort();
    };
  }, []);
  useEffect(() => {
    if (!currentUser || !activeWorkspaceId) return;
    let stale = false;
    if (contentIntent) {
      try { rememberCreationIntent(currentUser.uid, activeWorkspaceId, contentIntent); }
      catch { reportOnboardingFailure("handoff_storage", activeWorkspaceId); setError("Your browser couldn’t keep your content for setup. Enable session storage, or finish setup in this tab without reloading."); }
    }
    // Everyone makes a first post; ideas are found in the background after setup.
    api("/start", { mode: "create", ...(businessWebsite ? { businessWebsite } : {}), ...(incomingReturn ? { returnPath: incomingReturn } : {}) })
      .then(({ state: saved }) => {
        if (stale) return;
        if (saved.step === "complete") {
          navigate(incomingReturn || saved.returnPath || creationDestination(contentIntent), {
            replace: true,
          });
          return;
        }
        setState(saved);
        lastSavedDraft.current = JSON.stringify(saved.draft);
        setDraft(saved.draft);
        setAssets(saved.draftAssets || []);
        setUrl(saved.draft.website || "");
        setSourceUrl(
          saved.origin === "website" ? saved.draft.website || "" : "",
        );
        setDescription(saved.draft.businessDescription || "");
        if (saved.draft.businessDescription) setView("review");
      })
      .catch((err) => {
        if (!stale) setError(err.message);
      });
    return () => {
      stale = true;
    };
  }, [api, currentUser, activeWorkspaceId, navigate, contentIntent, incomingReturn, businessWebsite]);
  // Also refocus when the engine step advances to choosing a first post.
  const choosingFirstPost = !!state?.engineReady && ideasReady;
  useEffect(() => {
    heading.current?.focus();
  }, [state?.step, view, choosingFirstPost]);

  useEffect(() => {
    if (
      !draft ||
      state?.step !== "business" ||
      busy ||
      JSON.stringify(draft) === lastSavedDraft.current
    )
      return;
    // A newer edit (or a scan starting) cancels this save's pending retry, so
    // a retried older draft can never land on top of a newer save.
    const retry = new AbortController();
    const timer = window.setTimeout(() => {
      // A phone that backgrounded the tab drops the save; retry when it's back.
      void retryAfterReconnect(() => api("", { draft, origin: state.origin }, "PATCH"), retry.signal)
        .then((result) => {
          if (alive.current) {
            lastSavedDraft.current = JSON.stringify(draft);
            setState(result.state);
            setError(clearDraftSaveError);
          }
        })
        .catch(() => {
          if (alive.current && !retry.signal.aborted) setError(current => draftSaveErrors.has(current) ? current : autosaveError);
        });
    }, 700);
    return () => {
      window.clearTimeout(timer);
      retry.abort();
    };
  }, [draft, state, busy, api]);

  useEffect(() => {
    if (state?.step) reportOnboardingProgress("step_viewed", activeWorkspaceId, { step: state.step, view });
  }, [state?.step, view, activeWorkspaceId]);

  const socialFor = (handle: string | null | undefined): BusinessDraft["primarySocial"] =>
    handle ? { platform: "instagram", handle } : undefined;
  const updateDraft = (updates: Partial<BusinessDraft>) =>
    setDraft((prev) => (prev ? { ...prev, ...updates } : prev));
  const persist = async (
    next: BusinessDraft,
    origin = state?.origin || "manual",
    draftAssets = assets,
  ) => {
    const result = await retryAfterReconnect(() => api("", { draft: next, origin, draftAssets }, "PATCH"));
    if (alive.current) {
      lastSavedDraft.current = JSON.stringify(next);
      setState(result.state);
      setError(clearDraftSaveError);
    }
    return result.state as OnboardingState;
  };

  const extract = async () => {
    if (!currentUser || !state || !draft) return;
    let website: string;
    try {
      website = normalizeWebsite(url);
    } catch {
      setCrawlFailed(true);
      websiteInput.current?.focus();
      setWebsiteError("Enter a website address, like yourbusiness.com.");
      return;
    }
    const guidance = getBusinessWebsiteGuidance(website);
    if (guidance) {
      setCrawlFailed(true);
      setWebsiteError(guidance);
      return;
    }
    const attempt = ++extractionAttempt.current;
    extractionController.current?.abort();
    const controller = new AbortController();
    extractionController.current = controller;
    const progress = (stage: string) => {
      if (alive.current && attempt === extractionAttempt.current) setScanStage(stage);
    };
    let phase: "prepare" | "extract" | "save" = "prepare";
    setWebsiteError("");
    setScanStage("preparing");
    reportOnboardingProgress("scan_started", activeWorkspaceId);
    setError("");
    setCrawlFailed(false);
    setBusy("Reading your website and finding your brand…");
    try {
      const base = { ...draft, website };
      await persist(base, "manual");
      if (!alive.current || attempt !== extractionAttempt.current) return;
      phase = "extract";
      progress("reading");
      // Each scan has its own id, so a dropped one is picked up by that id and
      // never confused with an earlier or cancelled scan of the same kit.
      let scanId = "";
      const scan = async () => {
        scanId = globalThis.crypto?.randomUUID?.()
          ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
        return extractBrandKitFromUrl(
          website,
          await withRequestDeadline(() => currentUser.getIdToken()),
          state.kitId,
          activeWorkspaceId || undefined,
          controller.signal,
          progress,
          { keepResult: true, scanId },
        );
      };
      let result: BrandKitExtraction;
      const visibility = watchVisibility();
      try {
        result = await scan();
      } catch (err) {
        if (!alive.current || attempt !== extractionAttempt.current) return;
        const dropped =
          (isConnectionDrop(err) || (err as Error).name === "TimeoutError") &&
          connectionMayHaveDropped(visibility.wasHidden());
        if (dropped) {
          // The server keeps scanning when a backgrounded phone drops the
          // connection; pick up its result instead of losing it.
          setBusy("Picking up your website scan…");
          const kept = await recoverBrandKitScan({
            url: website,
            brandKitId: state.kitId,
            scanId,
            getIdToken: () => withRequestDeadline(() => currentUser.getIdToken()),
            workspaceId: activeWorkspaceId || undefined,
            signal: controller.signal,
          });
          if (!alive.current || attempt !== extractionAttempt.current) return;
          if (kept) result = kept;
          else {
            setBusy("Reading your website and finding your brand…");
            result = await scan();
          }
        } else if ((err as { code?: string }).code === "SITE_UNAVAILABLE") {
          setBusy("Your website is taking a little longer. Trying once more…");
          result = await scan();
        } else throw err;
      } finally {
        visibility.stop();
      }
      if (!alive.current || attempt !== extractionAttempt.current) return;
      const next: BusinessDraft = {
        ...base,
        brandName: result.brandName,
        website: result.website || base.website,
        tagline: result.tagline,
        businessDescription: result.businessDescription || result.tagline,
        targetAudiences: result.targetAudiences || [],
        colors: result.colors,
        fonts: result.fonts,
        logoPath: result.logoPath || "",
        brandVoiceTone: result.voiceTone,
        brandVoiceInstructions: result.voiceInstructions,
        // Only a handle they gave us themselves (the profile they scanned, or
        // their homepage link): a website's social links can belong to anyone,
        // and posts print the handle. Set on every scan, so an earlier one goes.
        primarySocial: socialFor(instagramHandle(website) && result.primarySocial?.platform === "instagram"
          ? result.primarySocial.handle
          : heroHandle),
      };
      setDraft(next);
      setAssets(result.brandAssets);
      setSourceUrl(website);
      setState(previous => previous ? { ...previous, origin: "website" } : previous);
      setView("review");
      phase = "save";
      progress("saving");
      await persist(next, "website", result.brandAssets);
      reportOnboardingProgress("scan_saved", activeWorkspaceId);
      if (result.assetWarning && alive.current && attempt === extractionAttempt.current) { reportOnboardingFailure("website_images_import", activeWorkspaceId); setError(imagesImportNotice); }
    } catch (err) {
      if (alive.current && attempt === extractionAttempt.current) {
        reportOnboardingFailure(phase === "extract" ? "website_import" : phase === "prepare" ? "website_prepare_save" : "website_result_save", activeWorkspaceId, undefined, err);
        setCrawlFailed(phase !== "save");
        setError(phase === "save"
          ? importedDraftSaveError
          : phase === "prepare"
            ? "We couldn’t save your website just now. Try again, or set up manually instead."
            : (err as Error).name === "TimeoutError"
              ? "Your website took too long to respond. Try again, or set up manually. It only takes a minute."
              : (err as { code?: string }).code === "SITE_BLOCKED"
                ? "This website blocks automated visits, so we couldn’t read it. Try another address, or set up manually. It only takes a minute."
                : ["INSTAGRAM_NO_WEBSITE", "INSTAGRAM_UNAVAILABLE"].includes((err as { code?: string }).code || "")
                  ? (err as Error).message
                  : "We couldn’t read your website. Check the address and try again, or set up manually.");
      }
    } finally {
      if (alive.current && attempt === extractionAttempt.current) { setBusy(""); setScanStage(null); }
    }
  };
  const validateOptionalWebsite = (website?: string) => {
    try {
      if (website?.trim()) normalizeWebsite(website);
      setWebsiteError("");
      return true;
    } catch {
      websiteInput.current?.focus();
      setWebsiteError(
        "Enter a full website address, like yourbusiness.com, or remove it to continue without one.",
      );
      return false;
    }
  };
  const manual = async () => {
    if (!draft) return;
    const missing = { name: !draft.brandName?.trim(), description: !description.trim() };
    if (missing.name || missing.description) {
      setManualMissing(missing);
      document.getElementById(missing.name ? "manual-name" : "manual-description")?.focus();
      return;
    }
    if (!validateOptionalWebsite(url)) return;
    const attempt = ++extractionAttempt.current;
    const controller = new AbortController();
    extractionController.current = controller;
    setPreparingDescription(true);
    setBusy("Preparing your description and audiences…");
    setError("");
    try {
      const next = {
        ...draft,
        businessDescription: description.slice(0, 2000),
        website: url.trim(),
        targetAudiences: [] as string[],
      };
      try {
        Object.assign(
          next,
          await api("/describe", { name: draft.brandName, description }, undefined, controller.signal),
        );
      } catch {
        if (!alive.current || attempt !== extractionAttempt.current) return;
        setError(audiencesNotice);
      }
      if (!alive.current || attempt !== extractionAttempt.current) return;
      setDraft(next);
      setView("review");
      await persist(next, "manual");
    } catch (err) {
      if (alive.current && attempt === extractionAttempt.current) setError(manualDraftSaveError);
    } finally {
      if (alive.current && attempt === extractionAttempt.current) { setBusy(""); setPreparingDescription(false); }
    }
  };
  const saveBrand = async (withoutImages = false) => {
    if (!draft || !currentUser || !state) return;
    if (!validateOptionalWebsite(draft.website)) return;
    setBusy("Saving your brand kit…");
    setError("");
    setAssetSaveFailed(false);
    let phase: "brand" | "assets" | "advance" = "brand";
    try {
      // A brand without chosen colors is saved without any, so posts keep the look's own palette.
      const next = {
        ...draft,
        targetAudiences: [
          ...(draft.targetAudiences || []),
          ...(audience.trim() ? [audience.trim()] : []),
        ].slice(0, 7),
      };
      // Persist edits first. Brand creation uses the stable server-issued ID, so retries update the same kit.
      if (savedBrand.current !== JSON.stringify(next)) {
        await persist(next);
        await api("/brand", { draft: next });
        savedBrand.current = JSON.stringify(next);
        setDraft(next);
        setAudience("");
      }
      phase = "assets";
      if (!withoutImages && assets.length && savedAssets.current !== JSON.stringify(assets)) {
        await commitBrandKitAssets(
          state.kitId,
          assets,
          await withRequestDeadline(() => currentUser.getIdToken()),
          [],
          activeWorkspaceId || undefined,
        );
        savedAssets.current = JSON.stringify(assets);
      }
      phase = "advance";
      const result = await api("", { step: "socials" }, "PATCH");
      if (alive.current) {
        setDraft(next);
        setAudience("");
        await leaveSocials(
          result.state,
          "Your brand kit is saved, but we couldn’t open the next step. Please try again.",
        );
      }
    } catch (err) {
      if (alive.current) {
        setAssetSaveFailed(phase === "assets");
        setError(phase === "assets"
          ? "Your brand is saved, but some images didn’t finish saving. Try again, or continue and check them later in Brand Kits."
          : phase === "advance"
            ? "Your brand kit is saved. We couldn’t open the next step, so press Continue to try again."
            : "We couldn’t confirm your brand kit was saved. Your edits are still here, and Continue will safely try again.");
      }
    } finally {
      if (alive.current) setBusy("");
    }
  };
  const cancelScan = () => {
    extractionAttempt.current++;
    extractionController.current?.abort();
    if (scanStage) reportOnboardingProgress("scan_cancelled", activeWorkspaceId);
    setScanStage(null);
    setPreparingDescription(false);
    setBusy("");
  };
  const skipBrand = async () => {
    cancelScan();
    reportOnboardingProgress("brand_skipped", activeWorkspaceId);
    setBusy("Saving your progress…");
    setError("");
    try {
      const result = await api("", { step: "socials", brandSkipped: true }, "PATCH");
      if (alive.current) await leaveSocials(result.state);
    } catch (err) { if (alive.current) setError((err as Error).message); }
    finally { if (alive.current) setBusy(""); }
  };
  // Accounts are connected when a post is published, not during setup, so
  // the server's "socials" step is passed through without stopping.
  const leaveSocials = async (
    current: OnboardingState,
    failureMessage = "We couldn’t open the next step. Please try again.",
  ) => {
    // Runs once per setup unless the user retries; the effect below must not
    // start a second, overlapping move.
    leftSavedSocials.current = true;
    if (contextual && !firstPostIntent(current)) { await finish(true); return; }
    setBusy(contextual ? "Opening your first post…" : "Preparing your content engine…");
    setError("");
    try {
      const result = await api("", { step: contextual ? "post" : "engine" }, "PATCH");
      if (!alive.current) return;
      // Without a brand, there is no business source to scan.
      if (!contextual && (current.brandSkipped || current.origin === "manual")) await finish(true);
      else setState(result.state);
    } catch {
      // Show the server's step (the pass-through screen with Try again)
      // rather than the brand form, where edits would autosave into a setup
      // that has already moved on.
      if (alive.current) {
        setState(current);
        setError(failureMessage);
      }
    } finally {
      if (alive.current) setBusy("");
    }
  };
  // Setups left at the old connect step (or reopened mid-way) move on once
  // any incoming link has been checked, since the first post depends on it.
  useEffect(() => {
    if (state?.step !== "socials" || needsContentCheck || leftSavedSocials.current) return;
    leftSavedSocials.current = true;
    void leaveSocials(state);
    // leaveSocials is recreated every render; this runs once per setup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, needsContentCheck]);
  const discover = useCallback(
    async (raw: string, owned: boolean) => {
      if (!currentUser || !activeWorkspaceId) return;
      const attempt = ++discoveryAttempt.current;
      setBusy("Finding content for your first ideas…");
      setIdeasProgress(null);
      setError("");
      try {
        const website = normalizeWebsite(raw);
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE}/api/autopilot/discover`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${await withRequestDeadline(() => currentUser.getIdToken())}`,
              "Content-Type": "application/json",
              "x-workspace-id": activeWorkspaceId,
            },
            body: JSON.stringify({ url: website, scope: "website" }),
          },
        );
        const result = await response.json();
        if (!alive.current || attempt !== discoveryAttempt.current) return;
        if (!response.ok)
          throw Object.assign(new Error(
            result.error ||
              "We couldn’t find content here. Try a blog or feed URL, or continue with your brand kit.",
          ), { fromServer: true });
        // Older servers may still return section choices. Keep the website
        // intact and use the existing bounded crawl instead of asking the user.
        // A redirect or Instagram link is followed under the address it led to.
        const { resolvedUrl, ...found } = result;
        const sourceUrl = resolvedUrl || website;
        const source = found.needsSectionConfirmation
          ? { sourceType: "website", url: sourceUrl }
          : { ...found, url: sourceUrl };
        const activation = await api("/activate", {
          source: { ...source, isOwned: owned },
        });
        if (!alive.current || attempt !== discoveryAttempt.current) return;
        setState((prev) =>
          prev
            ? {
                ...prev,
                engineReady: true,
                monitoringEnabled: activation.enabled,
              }
            : prev,
        );
        setAddingSource(false);
      } catch (err) {
        if (alive.current && attempt === discoveryAttempt.current) {
          // The discovery route explains site problems; anything else (network,
          // activation) gets one plain message instead of a raw error.
          setError((err as { fromServer?: boolean }).fromServer
            ? (err as Error).message
            : "We couldn’t reach your website just now. Try again, or point us to your blog.");
          setAddingSource(true);
        }
      } finally {
        if (alive.current && attempt === discoveryAttempt.current) setBusy("");
      }
    },
    [currentUser, activeWorkspaceId, api],
  );
  useEffect(() => {
    if (contextual || state?.brandSkipped || state?.step !== "engine" || initialScan.current) return;
    initialScan.current = true;
    if (
      !state.engineReady &&
      state.origin === "website" &&
      state.draft.website
    ) {
      setSourceUrl(state.draft.website);
      void discover(state.draft.website, true);
    }
  }, [state, discover, contextual]);
  useEffect(() => {
    if (state?.step !== "engine" || !state.engineReady) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const startedAt = Date.now();
    let reportedDelay = false;
    const poll = async () => {
      try {
        const result = await api("/ideas-status");
        if (cancelled) return;
        if (result.progress) setIdeasProgress(result.progress);
        if (result.ready) {
          setIdeasReady(true);
          return;
        }
      } catch {
        // Transient failures should not interrupt the scan or open an empty feed.
      }
      if (cancelled) return;
      if (Date.now() - startedAt >= 120000) {
        if (!reportedDelay) {
          reportOnboardingFailure("ideas_wait_exceeded", activeWorkspaceId);
          reportedDelay = true;
        }
      }
      timer = setTimeout(poll, 2000);
    };
    void poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [state?.step, state?.engineReady, api, activeWorkspaceId]);
  const waitingForIdeas = !!state?.engineReady && !ideasReady;
  // Without a link of their own, the first post is made from their website,
  // but only one we've read successfully (the brand came from scanning it).
  // An Instagram profile from the homepage gives way to that website, else
  // is made from its bio and captions when it has any.
  // Takes the setup state explicitly when called right after a save, before
  // this render's copy has caught up with it.
  const firstPostDestination = (current = state) => {
    const fallback = profileOnly && !sourceCheck?.instagramPosts ? "/generate" : destination;
    if (!contextual || (destination !== "/generate" && !profileOnly) || !current?.brandSaved || current.origin !== "website" || !current.draft.website) return fallback;
    try {
      return creationDestination(new URLSearchParams({ articleUrl: normalizeWebsite(current.draft.website) }).toString());
    } catch {
      return fallback;
    }
  };
  // The first post's starting content, when there is any to make it from.
  const firstPostIntent = (current = state) => {
    const to = firstPostDestination(current);
    if (!to.startsWith("/generate?")) return null;
    // "setup=ready" marks arriving on Generate after setup; the embedded composer
    // is still inside setup, so it must not clear the saved intent yet.
    const intent = new URLSearchParams(to.slice("/generate?".length));
    intent.delete("setup");
    return intent.toString();
  };
  // The source card and settings sit right below; this only says nothing is
  // required, and how long making the posts takes, before anyone waits on it.
  const firstPostSubtitle = () => {
    const intent = new URLSearchParams(firstPostIntent() || "");
    const hasSource = ["articleUrl", "inputText", "sourceDocument", "pendingContent"].some((key) => intent.get(key));
    return `${hasSource ? "Ready to go as is, or tweak the settings below." : "Add a link or some text to post about."} Your posts take a minute or two to make.`;
  };
  const finish = async (continueWithoutIdeas = false, deferIdeas = false, to?: string) => {
    if (waitingForIdeas && !continueWithoutIdeas) return;
    const deferredIdeaSource = deferIdeas && !state?.engineReady
      ? { url: normalizeWebsite(sourceUrl || state?.draft.website || ""), isOwned: sourceOwned }
      : undefined;
    if (deferredIdeaSource) {
      // The server takes over discovery. Ignore late browser results so they
      // cannot start a second scan or clear the completion/retry UI.
      discoveryAttempt.current++;
      setDeferredIdeas(true);
    }
    setBusy("Opening your workspace…");
    setError("");
    try {
      const completionBody = deferredIdeaSource ? { deferredIdeaSource } : {};
      // Activation may still be in flight. Completion hands its recovery to
      // the worker, which honors the activation lease before doing any work.
      if (deferredIdeaSource && currentUser && activeWorkspaceId)
        await onboardingRequest(currentUser, activeWorkspaceId, "/complete", completionBody);
      else await api("/complete", completionBody);
      void refreshCustomerData().catch(() => reportOnboardingFailure("account_refresh", activeWorkspaceId));
      reportOnboardingProgress("completed", activeWorkspaceId);
      if (alive.current) {
        window.scrollTo(0, 0);
        navigate(to || firstPostDestination(), { replace: true, ...(to === "/created" ? { state: { fromGeneration: true } } : {}) });
      }
    } catch (err) {
      if (alive.current) setError((err as Error).message);
    } finally {
      if (alive.current) setBusy("");
    }
  };
  const openingWorkspace = useRef(false);
  const openWorkspaceWithFirstPost = useCallback(
    async (): Promise<boolean> => {
      // Completing setup twice would queue suggestions twice.
      if (openingWorkspace.current) return true;
      openingWorkspace.current = true;
      setBusy("Opening your workspace…");
      setError("");
      try {
        await api("/complete", {});
        void refreshCustomerData().catch(() => reportOnboardingFailure("account_refresh", activeWorkspaceId));
        if (alive.current) {
          window.scrollTo(0, 0);
          // Land in Drafts, where the first post is being made.
          navigate("/created", { replace: true, state: { fromGeneration: true } });
        }
        return true;
      } catch (err) {
        openingWorkspace.current = false;
        if (alive.current) setError((err as Error).message);
        return false;
      } finally {
        if (alive.current) setBusy("");
      }
    },
    [api, navigate, refreshCustomerData, activeWorkspaceId],
  );
  const trackUpload = (target: "logo" | "images", label?: string) =>
    ({ loaded, total }: UploadProgress) => {
      if (!total || !alive.current) return;
      const percent = Math.min(100, Math.round((loaded / total) * 100));
      setUploadProgress(previous =>
        previous?.target === target && previous.percent === percent && previous.label === label
          ? previous
          : { target, percent, label });
    };
  const uploadLogo = async (file?: File) => {
    if (!file || !currentUser || !activeWorkspaceId || !state || !draft) return;
    setLogoUploadError("");
    if (!["image/png", "image/jpeg", "image/webp", "image/svg+xml"].includes(file.type)) {
      setLogoUploadError("Choose a PNG, JPG, WebP or SVG file.");
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      setLogoUploadError("Choose a logo smaller than 5 MB.");
      return false;
    }
    setBusy("Uploading your logo…");
    try {
      const result = await uploadBrandKitVisualAsset(
        activeWorkspaceId,
        state.kitId,
        file,
        await withRequestDeadline(() => currentUser.getIdToken()),
        { skipPreview: true, onProgress: trackUpload("logo") },
      );
      if (!alive.current) return;
      const next = { ...draft, logoPath: result.path };
      setDraft(next);
      // The logo is uploaded. If this save fails, autosave retries the draft
      // and reports it the same way as any other edit.
      await persist(next).catch(() => undefined);
      return true;
    } catch (err) {
      reportOnboardingFailure("logo_upload", activeWorkspaceId, undefined, err);
      if (alive.current) setLogoUploadError(
        isTimeoutError(err)
          ? "The upload stalled. Check your connection and try again."
          : err instanceof TypeError
            ? "We couldn’t upload your logo. Check your connection and try again."
            : "We couldn’t upload your logo. Try again, or use a different PNG, JPG, WebP or SVG file.",
      );
      return false;
    } finally {
      if (alive.current) {
        setBusy("");
        setUploadProgress(null);
      }
    }
  };
  const uploadImages = async (files: File[], retrySave = false) => {
    if (
      (!files.length && !retrySave) ||
      !currentUser ||
      !activeWorkspaceId ||
      !state ||
      !draft
    )
      return;
    setBusy(retrySave ? "Saving images…" : "Uploading images…");
    setImageUploadError("");
    setImageSaveFailed(false);
    // Keep failures from previous selections, but hide this batch's errors until
    // every file and its automatic retry have finished.
    setFailedImages(previous => previous.filter(item => !files.includes(item.file)));
    type FailedImage = { file: File; message: string; retryable: boolean };
    const failures: FailedImage[] = [];
    const retryFiles: File[] = [];
    let next = assets;
    let token: string | undefined;
    let saveError: unknown;
    let needsSave = imageSaveFailed || retrySave;
    const canRetry = (err: unknown) => {
      const status = (err as { status?: number })?.status;
      if (status !== undefined) return status === 408 || status === 429 || status >= 500;
      return err instanceof TypeError || isTimeoutError(err);
    };
    const recordFailure = (file: File, message: string, retryable = true) => {
      failures.push({ file, message, retryable });
    };
    const saveUploadedImages = async () => {
      try {
        await persist(draft, state.origin, next);
        needsSave = false;
        saveError = undefined;
      } catch (err) {
        needsSave = true;
        saveError = err;
      }
    };
    const uploadFile = async (file: File, retry: boolean) => {
      if (!alive.current) return;
      if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
        recordFailure(file, "Choose a PNG, JPG, or WebP image.", false);
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        recordFailure(file, "Choose an image up to 10 MB.", false);
        return;
      }
      if (next.length >= 30) {
        recordFailure(file, "You can keep up to 30 images. Remove one to add this image.");
        return;
      }
      try {
        token = token || await withRequestDeadline(() => currentUser.getIdToken());
        if (!alive.current) return;
        setUploadProgress(null);
        const label = files.length > 1 ? `Image ${files.indexOf(file) + 1} of ${files.length}` : undefined;
        const { asset } = await uploadBrandKitAsset(state.kitId, file, token, true, activeWorkspaceId,
          { onProgress: trackUpload("images", label) });
        if (!alive.current) return;
        next = [...next, asset];
        setAssets(next);
      } catch (err) {
        if (!retry && canRetry(err)) {
          retryFiles.push(file);
          return;
        }
        reportOnboardingFailure("image_upload", activeWorkspaceId, (err as { status?: number })?.status, err);
        recordFailure(file, isTimeoutError(err)
          ? "The upload stalled. Check your connection and try again."
          : getBrandKitAssetUploadErrorMessage(err));
        return;
      }
      // Preserve progress after each success without displaying intermediate
      // save errors. A later save includes every successful upload so far.
      await saveUploadedImages();
    };
    try {
      if (retrySave) await saveUploadedImages();
      for (const file of files) await uploadFile(file, false);
      if (!alive.current) return;
      if (retryFiles.length) {
        setBusy("Retrying images…");
        await new Promise(resolve => window.setTimeout(resolve, 500));
        for (const file of retryFiles) await uploadFile(file, true);
      }
      if (!alive.current) return;
      if (needsSave && (!saveError || canRetry(saveError))) {
        setBusy("Saving images…");
        await new Promise(resolve => window.setTimeout(resolve, 500));
        if (!alive.current) return;
        await saveUploadedImages();
      }
      if (!alive.current) return;
      setFailedImages(previous => [...previous, ...failures]);
      if (needsSave) {
        reportOnboardingFailure("image_save", activeWorkspaceId, (saveError as { status?: number })?.status, saveError);
        setImageSaveFailed(true);
        setImageUploadError("Your images uploaded, but we couldn’t save your progress. Keep this tab open and retry saving.");
      }
    } finally {
      if (alive.current) {
        setBusy("");
        setUploadProgress(null);
      }
    }
  };
  const postStep = state?.step === "post";
  const step = state?.step === "socials" ? 1 : state?.step === "engine" || postStep ? 2 : 0;
  const showIdeasReady = ideasReady;
  // Business setup ends by making one post from the ideas just found.
  const showFirstPost =
    step === 2 && !contextual && !!state?.engineReady && ideasReady && !addingSource;
  const navStep = showFirstPost ? 2 : step > 0 ? 1 : 0;
  // Discovery starts in an effect. Render its pending state immediately so
  // the first engine frame does not flash the idle screen and enabled CTA.
  const automaticDiscoveryPending =
    step === 2 && !contextual && !initialScan.current &&
    !state?.brandSkipped && !state?.engineReady && state?.origin === "website" && !!state.draft.website;
  const showIdeasLoading =
    !!busy || waitingForIdeas || automaticDiscoveryPending || deferredIdeas;
  const ideasLoadingVisible = step === 2 && !contextual && !ideasReady && showIdeasLoading;
  useEffect(() => {
    if (!ideasLoadingVisible) {
      setIdeasDelayed(false);
      return;
    }
    // One continuous minute from the first loading frame, including discovery.
    // Moving from discovery to extraction must not restart the clock.
    const timer = setTimeout(() => setIdeasDelayed(true), 60000);
    return () => clearTimeout(timer);
  }, [ideasLoadingVisible]);
  const connectedAccounts = connections
    .filter((connection) => connection.status === "active")
    .sort((a, b) => b.createdAt - a.createdAt);
  const title =
    step === 1
      ? contextual
        ? "Make your first post."
        : state?.brandSkipped
          ? "Let’s get your workspace ready."
          : "Your brand is ready. Let’s put it to work."
      : showFirstPost
        ? "Your first post"
        : postStep
        ? "Make your first post."
        : step === 2
        ? state?.engineReady
          ? showIdeasReady
            ? "Your post suggestions are ready."
            : "Preparing your post suggestions."
          : "Your brand is ready. Let’s put it to work."
        : view === "review"
          ? "Review your brand kit."
          : view === "manual"
            ? "Tell us about your brand."
            : contextual
              ? restoredContent
                ? "Make your first post feel like you."
                : "Let’s make your first post."
              : "Your social media engine starts here.";
  const subtitle =
    postStep
      ? firstPostSubtitle()
      : step === 1
      ? ""
      : view === "review"
        ? state?.origin === "website"
          ? "We put this together from your website. Adjust anything that’s off, or just continue."
          : "Drafted from your description. Adjust anything that’s off, or just continue."
        : view === "manual"
          ? "A few sentences is plenty. We’ll suggest the audience and a voice for you to review."
          : contextual
            ? restoredContent
              ? "Add your business website and we’ll bring your logo, colors and voice into the post. No website? You can skip this."
              : "Add your website. We’ll bring in your logo, colors and voice, make your first post from it, and keep finding ideas for more."
            : "Add your website. Wavegen will learn about your business, capture your brand, and find content to turn into social posts.";

  // One position and treatment across setup: directly before recovery actions.
  const visibleError = error;
  useEffect(() => {
    // Action failures on a long review must be visible without hunting below
    // the fold. Background autosave never moves the user's reading position.
    // Informational notes never block, so they don’t pull the reader away either.
    if (visibleError && visibleError !== autosaveError && !infoNotices.has(visibleError)) errorNode.current?.scrollIntoView?.({ block: "nearest" });
  }, [visibleError, step, view]);
  const errorAlert = visibleError ? (
    <SetupNotice ref={errorNode} tone={infoNotices.has(visibleError) ? "info" : "error"}>
      <p>{visibleError}</p>
    </SetupNotice>
  ) : null;

  // The material they arrived with, shown the way Generate lists a source.
  // It isn't a post yet, so nothing here says "ready".
  const carried = (() => {
    // Nothing to show without an actual source (e.g. a lost handoff).
    if (!contextual || step > 1 || !restoredContent || profileOnly) return null;
    const content = new URLSearchParams(restoredContent);
    const checked = sourceCheck;
    const unchecked = checked?.status === "unverified";
    const pdf = content.has("sourceDocument") || content.get("source") === "pdf-to-carousel" || checked?.kind === "pdf";
    const link = content.get("articleUrl");
    const text = content.get("inputText");
    let host = "";
    try { if (link) host = new URL(link).hostname; } catch { /* Show the raw link instead. */ }
    const favicon = host && !pdf ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=32` : "";
    const video = link && !text ? checked?.youtube : undefined;
    return (
      <div className="mb-9 max-w-xl">
        <SourceCard
          icon={pdf || text ? FileText : link ? LinkIcon : content.has("templateId") ? LayoutTemplate : FileText}
          favicon={favicon}
          thumbnailUrl={video?.thumbnailUrl}
          quote={text || undefined}
          title={checked?.title || (link ? displayLink(link) : pdf ? "Your PDF" : content.has("templateId") ? "Your chosen template" : "Your content")}
          details={video
            ? [[video.channel, formatVideoDuration(video.durationSec)].filter(Boolean).join(" · ")]
            : link && checked?.title ? [displayLink(link)] : []}
          status={unchecked ? "unchecked" : "ok"}
          note={unchecked && (
            <p className="mt-0.5 text-xs text-amber-700">
              Not checked yet. You can swap it when you create your post.
            </p>
          )}
        />
      </div>
    );
  })();

  const intro = (
    <div className="max-w-2xl">
      {carried}
      <h1
        tabIndex={-1}
        ref={heading}
        className={step === 2 && !postStep ? "sr-only" : "onboarding-display text-[2.1rem] outline-none sm:text-5xl"}
      >
        {title}
      </h1>
      {(step !== 2 || postStep) && subtitle && (
        <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
          {subtitle}
        </p>
      )}
      {busy && !scanStage &&
        step !== 2 &&
        !["Uploading your logo…", "Uploading images…", "Retrying images…", "Saving images…"].includes(busy) && (
          <p
            role="status"
            aria-live="polite"
            className="mt-5 flex items-center gap-2 text-sm text-muted-foreground"
          >
            <Loader2 className="h-4 w-4 animate-spin text-brand motion-reduce:animate-none" />
            {busy}
          </p>
        )}
    </div>
  );
  const quiet = "h-10 rounded-md px-4 text-muted-foreground hover:text-foreground";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-foreground/[0.06]">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-16 gap-y-5 px-5 py-5 sm:px-10">
          <div className="flex items-center gap-2">
            <img src="/icon.svg" alt="" className="h-7 w-7" />
            <span className="onboarding-display text-xl">Wavegen</span>
          </div>
          <div className="min-w-0 flex-1 basis-full sm:ml-auto sm:max-w-xl sm:basis-auto">
            <SetupProgress
              current={navStep}
              steps={contextual ? ["Your brand", "Create your post"] : ["Your business", "Your content engine", "Your first post"]}
            />
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-5 pb-24 pt-10 sm:px-10 sm:pt-16">
        {!state || !draft ? (
          <div role={error ? undefined : "status"} className="flex min-h-[40vh] flex-col items-center justify-center gap-5 text-center">
            {error ? (
              <>
                {errorAlert}
                <Button variant="brand" className={pill} onClick={() => window.location.reload()}>
                  Try again
                </Button>
              </>
            ) : (
              <>
                <Loader2 aria-hidden="true" className="h-6 w-6 animate-spin text-brand motion-reduce:animate-none" />
                <p className="text-muted-foreground">Getting your workspace ready…</p>
              </>
            )}
          </div>
        ) : needsContentCheck ? (
          <IncomingContentCheck key={restoredContent} intent={restoredContent!} check={checkIncoming} onResolved={resolveIncoming} onReplace={replaceIncoming} />
        ) : (
          <div key={`${step}-${view}-${showFirstPost}`} className="onboarding-rise">
            {step === 0 && view === "website" && (
              <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)] lg:items-start lg:gap-16">
                <div>
                  {intro}
                  <form
                    className="mt-10 max-w-xl"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void extract();
                    }}
                  >
                    <Label htmlFor="website">Your business website</Label>
                    <div
                      className={cn(
                        "mt-2.5 flex flex-col gap-2 ring-offset-background sm:flex-row sm:items-center sm:rounded-lg sm:border sm:bg-card sm:p-1.5 sm:shadow-sm sm:focus-within:ring-2 sm:focus-within:ring-ring sm:focus-within:ring-offset-2",
                        websiteError && "sm:border-destructive/60",
                      )}
                    >
                      <div className="relative min-w-0 flex-1">
                        <Globe aria-hidden="true" className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          id="website"
                          ref={websiteInput}
                          aria-invalid={!!websiteError}
                          aria-describedby={websiteError ? "website-error" : undefined}
                          autoFocus
                          value={url}
                          onChange={(e) => {
                            setUrl(e.target.value);
                            setWebsiteError("");
                            updateDraft({ website: e.target.value });
                          }}
                          disabled={!!busy}
                          placeholder="yourbusiness.com"
                          autoComplete="url"
                          inputMode="url"
                          className={cn(
                            "h-12 rounded-md bg-card pl-12 text-base shadow-sm sm:border-0 sm:shadow-none sm:focus-visible:ring-0 sm:focus-visible:ring-offset-0",
                            websiteError && "border-destructive/60",
                          )}
                        />
                      </div>
                      <Button
                        // After a failed scan, manual setup becomes the main way forward.
                        variant={crawlFailed ? "secondary" : "brand"}
                        className={cn(pill, "h-12 shrink-0")}
                        disabled={!!busy || !url.trim()}
                        type="submit"
                      >
                        {scanStage && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />}
                        {scanStage ? "Setting up…" : crawlFailed ? "Try website again" : "Set it up for me"}
                        {!scanStage && <ArrowRight aria-hidden="true" className="ml-2 h-4 w-4" />}
                      </Button>
                    </div>
                    {websiteError && <p id="website-error" role="alert" className="mt-2.5 pl-1 text-sm text-destructive">{websiteError}</p>}
                    {errorAlert && <div className="mt-5">{errorAlert}</div>}
                    <div className="mt-6 flex flex-wrap items-center gap-x-1 gap-y-2">
                      <Button
                        type="button"
                        variant={crawlFailed ? "brand" : "ghost"}
                        className={crawlFailed ? cn(pill, "mr-1") : quiet}
                        onClick={() => {
                          const fallback = crawlFailed || !!scanStage;
                          cancelScan();
                          setError("");
                          setWebsiteError("");
                          let keptWebsite = "";
                          // An Instagram profile they typed is their handle, not their website.
                          const typedHandle = instagramHandle(url);
                          if (fallback && !typedHandle && !getBusinessWebsiteGuidance(url)) {
                            try { keptWebsite = normalizeWebsite(url); } catch { /* Invalid input must not block manual setup. */ }
                          }
                          setShowManualWebsite(!!keptWebsite);
                          setUrl(keptWebsite);
                          updateDraft({ website: keptWebsite, primarySocial: socialFor(typedHandle || heroHandle) });
                          setView("manual");
                        }}
                      >
                        {crawlFailed || scanStage ? "Set up manually" : "I don’t have a website"}
                        {crawlFailed && <ArrowRight aria-hidden="true" className="ml-2 h-4 w-4" />}
                      </Button>
                      <Button type="button" variant="ghost" className={quiet} disabled={!!busy && !scanStage} onClick={() => void skipBrand()}>
                        Skip brand setup
                      </Button>
                    </div>
                  </form>
                </div>
                <BrandScanPanel stage={scanStage} findsIdeas={!contextual} className={scanStage ? "" : "hidden lg:block"} />
              </div>
            )}

            {step === 0 && view === "manual" && (
              <div>
                <div>
                  {intro}
                  <form
                    className="mt-10 max-w-xl space-y-6"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void manual();
                    }}
                  >
                    {showManualWebsite && <div className="space-y-2">
                      <Label htmlFor="manual-website">Website (optional)</Label>
                      <div className="flex gap-2">
                        <Input ref={websiteInput} id="manual-website" className="h-11" aria-invalid={!!websiteError} aria-describedby={websiteError ? "manual-website-error" : undefined} value={url} disabled={!!busy} onChange={e => { setUrl(e.target.value); updateDraft({ website: e.target.value }); setWebsiteError(""); }} />
                        <Button type="button" variant="ghost" size="icon" className="h-11 w-11 shrink-0 text-muted-foreground hover:text-destructive" aria-label="Remove website" title="Remove website" disabled={!!busy} onClick={() => { setUrl(""); updateDraft({ website: "" }); setWebsiteError(""); setShowManualWebsite(false); }}><Trash2 className="h-4 w-4" aria-hidden="true" /></Button>
                      </div>
                      {websiteError && <p id="manual-website-error" role="alert" className="text-sm text-destructive">{websiteError}</p>}
                    </div>}
                    <div className="space-y-2">
                      <Label htmlFor="manual-name">Brand name</Label>
                      <Input
                        id="manual-name"
                        className="h-11"
                        value={draft.brandName || ""}
                        maxLength={120}
                        aria-invalid={manualMissing.name}
                        aria-describedby={manualMissing.name ? "manual-name-error" : undefined}
                        onChange={(e) => {
                          updateDraft({ brandName: e.target.value });
                          setManualMissing(current => ({ ...current, name: false }));
                        }}
                        disabled={!!busy}
                      />
                      {manualMissing.name && <p id="manual-name-error" role="alert" className="text-sm text-destructive">Add your brand name.</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="manual-description">
                        What is your brand about, and who is it for?
                      </Label>
                      <Textarea
                        id="manual-description"
                        rows={5}
                        maxLength={2000}
                        value={description}
                        aria-invalid={manualMissing.description}
                        aria-describedby={manualMissing.description ? "manual-description-error" : undefined}
                        onChange={(e) => {
                          setDescription(e.target.value);
                          updateDraft({ businessDescription: e.target.value });
                          setManualMissing(current => ({ ...current, description: false }));
                        }}
                        placeholder="For example: We help small business owners understand their finances and plan for growth."
                        disabled={!!busy}
                      />
                      {manualMissing.description && <p id="manual-description-error" role="alert" className="text-sm text-destructive">Add a sentence or two about what you do and who it’s for.</p>}
                    </div>
                    {errorAlert}
                    <div className="flex flex-wrap items-center gap-2">
                      <Button variant="brand" className={pill} disabled={!!busy} type="submit">
                        {preparingDescription && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />}
                        Continue
                        {!preparingDescription && <ArrowRight aria-hidden="true" className="ml-2 h-4 w-4" />}
                      </Button>
                      <Button variant="ghost" className={quiet} disabled={!!busy && !preparingDescription} type="button" onClick={() => void skipBrand()}>
                        Skip brand setup
                      </Button>
                    </div>
                    <button
                      type="button"
                      disabled={!!busy}
                      className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
                      onClick={() => {
                        setView("website");
                        setError("");
                      }}
                    >
                      <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                      Use a website instead
                    </button>
                  </form>
                </div>
              </div>
            )}

            {step === 0 && view === "review" && (
              <>
                {intro}
                <div className="mt-12">
                <div id="brand-review-details" className="grid gap-x-16 gap-y-14 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
                  <form
                    id="onboarding-brand-form"
                    className="space-y-14"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void saveBrand();
                    }}
                  >
                    <ReviewGroup id="review-business" title="Business" hint="The basics every post builds on.">
                      <div className="space-y-2">
                        <Label htmlFor="brand-name">Brand name</Label>
                        <Input
                          id="brand-name"
                          className="h-11"
                          value={draft.brandName || ""}
                          onChange={(e) =>
                            updateDraft({ brandName: e.target.value })
                          }
                          required
                          maxLength={120}
                          disabled={!!busy}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="business-description">
                          About your business
                        </Label>
                        <Textarea
                          id="business-description"
                          rows={5}
                          maxLength={2000}
                          value={draft.businessDescription || ""}
                          onChange={(e) =>
                            updateDraft({ businessDescription: e.target.value })
                          }
                          required
                          disabled={!!busy}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="brand-website">Website (optional)</Label>
                        <div className="flex gap-2">
                          <Input
                            id="brand-website"
                            className="h-11"
                            ref={websiteInput}
                            aria-invalid={!!websiteError}
                            aria-describedby={websiteError ? "brand-website-error" : undefined}
                            value={draft.website || ""}
                            disabled={!!busy}
                            onChange={(e) => {
                              setWebsiteError("");
                              updateDraft({ website: e.target.value });
                            }}
                            placeholder="yourbusiness.com"
                          />
                          {websiteError && <Button type="button" variant="ghost" size="icon" aria-label="Remove website" title="Remove website" className="h-11 w-11 shrink-0 text-muted-foreground hover:text-destructive" onClick={() => { setUrl(""); updateDraft({ website: "" }); setWebsiteError(""); }}><Trash2 className="h-4 w-4" aria-hidden="true" /></Button>}
                        </div>
                        {websiteError && <p id="brand-website-error" role="alert" className="text-sm text-destructive">{websiteError}</p>}
                      </div>
                    </ReviewGroup>
                    <ReviewGroup id="review-audience" title="Audience" hint="Who your posts speak to.">
                      <Label htmlFor="new-audience" className="sr-only">Add an audience</Label>
                      <div className="space-y-3">
                        {(draft.targetAudiences || []).map((value, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <Textarea
                              rows={2}
                              className="min-h-0 resize-none leading-relaxed"
                              aria-label={`Audience ${index + 1}`}
                              value={value}
                              maxLength={240}
                              disabled={!!busy}
                              onChange={(e) =>
                                updateDraft({
                                  targetAudiences: draft.targetAudiences!.map(
                                    (item, i) =>
                                      i === index ? e.target.value : item,
                                  ),
                                })
                              }
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="shrink-0 text-muted-foreground hover:text-destructive"
                              disabled={!!busy}
                              aria-label={`Remove audience ${index + 1}`}
                              onClick={() =>
                                updateDraft({
                                  targetAudiences: draft.targetAudiences!.filter(
                                    (_, i) => i !== index,
                                  ),
                                })
                              }
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        {(draft.targetAudiences || []).length < 7 && (
                          <div className="flex gap-2">
                            <Input
                              id="new-audience"
                              className="h-11"
                              placeholder="Add an audience (optional)"
                              value={audience}
                              maxLength={240}
                              disabled={!!busy}
                              onChange={(e) => setAudience(e.target.value)}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-11 w-11 shrink-0"
                              aria-label="Add audience"
                              disabled={!!busy || !audience.trim()}
                              onClick={() => {
                                updateDraft({
                                  targetAudiences: [
                                    ...(draft.targetAudiences || []),
                                    audience.trim(),
                                  ],
                                });
                                setAudience("");
                              }}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </ReviewGroup>
                  </form>
                  <div className="min-w-0 space-y-14">
                  <ReviewGroup id="review-look" title="Look" hint="Used across your post designs.">
                    <div className="flex flex-wrap items-center gap-4">
                      {logoUrl ? (
                        <button
                          type="button"
                          aria-label="Resize logo"
                          title="Resize and position logo"
                          disabled={!!busy}
                          className={cn("relative grid h-20 w-20 shrink-0 place-items-center rounded-full border p-1.5 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2", logoSurfaceClass(logoTone))}
                          onClick={() => setResizingLogo(true)}
                        >
                          <img
                            src={logoUrl}
                            alt={`${draft.brandName || "Business"} logo`}
                            className="h-full w-full rounded-full object-contain"
                          />
                          <span
                            aria-hidden="true"
                            className="absolute -bottom-0.5 -right-0.5 rounded-full border bg-background p-1 text-muted-foreground"
                          >
                            <Scan className="h-3 w-3" />
                          </span>
                        </button>
                      ) : (
                        <span aria-hidden="true" className="grid h-20 w-20 shrink-0 place-items-center rounded-full border border-dashed border-foreground/15 text-muted-foreground">
                          <ImagePlus className="h-6 w-6" />
                        </span>
                      )}
                      <div className="min-w-0 flex-1 space-y-2">
                        <p className="text-sm font-medium">
                          Logo
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="rounded-md px-4"
                            disabled={!!busy}
                            onClick={() => logoInput.current?.click()}
                          >
                            {busy === "Uploading your logo…" ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden />
                            ) : (
                              <Upload className="mr-2 h-4 w-4" aria-hidden />
                            )}
                            {draft.logoPath ? "Replace logo" : "Upload logo"}
                          </Button>
                          {draft.logoPath && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="rounded-md text-muted-foreground hover:text-destructive"
                              aria-label="Remove logo"
                              title="Remove logo"
                              disabled={!!busy}
                              onClick={() => { setLogoUploadError(""); updateDraft({ logoPath: "" }); }}
                            >
                              <Trash2 className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          )}
                        </div>
                        {uploadProgress?.target === "logo" && (
                          <UploadProgressBar percent={uploadProgress.percent} />
                        )}
                        {logoUploadError ? (
                          <p role="alert" className="text-sm text-destructive">
                            {logoUploadError}
                          </p>
                        ) : !draft.logoPath && uploadProgress?.target !== "logo" && (
                          <p className="text-xs text-muted-foreground">
                            Optional. Posts leave the logo out until you add one.
                          </p>
                        )}
                        {draft.logoPath && !logoUrl && (
                          <p
                            role="status"
                            className="text-xs text-muted-foreground"
                          >
                            {logoError
                              ? "We couldn’t show this logo. Try replacing it."
                              : "Loading your logo…"}
                          </p>
                        )}
                      </div>
                        <input
                          ref={logoInput}
                          id="brand-logo"
                          aria-label="Upload brand logo"
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/svg+xml"
                          disabled={!!busy}
                          className="hidden"
                          onChange={(e) => {
                            void uploadLogo(e.target.files?.[0]);
                            e.target.value = "";
                          }}
                        />
                    </div>
                    <fieldset disabled={!!busy} className="min-w-0">
                      <legend className="mb-3 text-sm font-medium">
                        Brand colors
                      </legend>
                      <BrandPaletteEditor
                        colors={draft.colors}
                        onChange={(colors) => {
                          if (!busy) updateDraft({ colors });
                        }}
                      />
                    </fieldset>
                  </ReviewGroup>
                  <ReviewGroup id="brand-voice-title" title="Voice" hint="Shapes the language and personality of every post.">
                        <div className="space-y-2">
                          <Label htmlFor="voice">
                            Tone
                          </Label>
                          <select
                            id="voice"
                            disabled={!!busy}
                            value={draft.brandVoiceTone || ""}
                            onChange={(e) =>
                              updateDraft({
                                brandVoiceTone: (e.target.value ||
                                  null) as BusinessDraft["brandVoiceTone"],
                              })
                            }
                            className="h-11 w-full rounded-md border bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          >
                            <option value="">Natural</option>
                            <option value="friendly">Friendly</option>
                            <option value="professional">Professional</option>
                            <option value="casual">Casual</option>
                            <option value="formal">Formal</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="voice-description">
                            Voice description
                          </Label>
                          <Textarea
                            id="voice-description"
                            rows={4}
                            maxLength={1000}
                            disabled={!!busy}
                            value={draft.brandVoiceInstructions || ""}
                            placeholder="For example: Warm and direct. Use everyday language, share practical advice, and avoid jargon."
                            onChange={(e) =>
                              updateDraft({
                                brandVoiceInstructions: e.target.value,
                              })
                            }
                          />
                        </div>
                  </ReviewGroup>
                  </div>
                    <input
                      ref={imageInput}
                      type="file"
                      multiple
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      aria-label="Upload brand images"
                      disabled={!!busy}
                      onChange={(event) => {
                        const files = Array.from(event.target.files || []);
                        event.target.value = "";
                        void uploadImages(files);
                      }}
                    />
                  <ReviewGroup
                    className="lg:col-span-2"
                    id="brand-images-title"
                    title={assets.length ? "Your brand images" : "Photos"}
                    hint={assets.length
                      ? "Wavegen can use these in your posts. Remove any you don’t want."
                      : <><span>Have photos of your business?</span> Your products, your team, your workspace or behind-the-scenes moments make posts feel like yours.</>}
                  >
                    <div className="flex flex-wrap items-center gap-3">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="rounded-md px-4"
                            disabled={
                              !!busy || assets.length >= 30
                            }
                            onClick={() => imageInput.current?.click()}
                          >
                            {["Uploading images…", "Retrying images…", "Saving images…"].includes(busy) ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                            ) : (
                              <Upload className="mr-2 h-4 w-4" />
                            )}
                            {busy === "Uploading images…" ? "Uploading…"
                              : busy === "Retrying images…" ? "Retrying…"
                              : busy === "Saving images…" ? "Saving…"
                              : "Upload images"}
                          </Button>
                      {assets.length > 0 ? (
                        <span className="text-sm text-muted-foreground">
                          {assets.length} {assets.length === 1 ? "image" : "images"}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">Optional · PNG, JPG or WebP, at least 400 × 300 px, up to 10 MB each</span>
                      )}
                    </div>
                      {uploadProgress?.target === "images" && (
                        <UploadProgressBar percent={uploadProgress.percent} label={uploadProgress.label} />
                      )}
                      {imageUploadError && (
                        <SetupNotice
                          action={imageSaveFailed && (
                            <Button type="button" variant="outline" size="sm" className="rounded-md border-destructive/30 bg-card px-4 text-foreground" disabled={!!busy}
                              onClick={() => void uploadImages([], true)}>
                              Retry
                            </Button>
                          )}
                        >
                          {imageUploadError}
                        </SetupNotice>
                      )}
                      {failedImages.length > 0 && (
                        <ul className="space-y-2" aria-label="Images that need attention">
                          {failedImages.map((failure, index) => (
                            <li key={index} className="flex flex-wrap items-center gap-3 rounded-xl border border-destructive/25 bg-destructive/[0.04] py-2 pl-4 pr-2">
                              <AlertCircle className="h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
                              <div role="alert" className="min-w-0 flex-1 basis-40 text-sm">
                                <p className="break-all font-medium">{failure.file.name}</p>
                                <p className="text-destructive">{failure.message}</p>
                              </div>
                              <div className="flex items-center gap-1">
                                {failure.retryable && (
                                  <Button type="button" variant="outline" size="sm" className="rounded-md bg-card px-4" disabled={!!busy}
                                    aria-label={`Retry ${failure.file.name}`}
                                    onClick={() => void uploadImages([failure.file])}>
                                    Retry
                                  </Button>
                                )}
                                <Button type="button" variant="ghost" size="icon" disabled={!!busy}
                                  className="h-11 w-11 shrink-0 text-muted-foreground" aria-label={`Dismiss ${failure.file.name}`}
                                  onClick={() => setFailedImages(previous => previous.filter(item => item !== failure))}>
                                  <X className="h-4 w-4" aria-hidden="true" />
                                </Button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                      {assets.length > 0 ? (
                        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                          {assets.map((asset, index) => (
                            <BrandImageReview
                              key={asset.id}
                              asset={asset}
                              index={index}
                              disabled={!!busy}
                              onRemove={async () => {
                                setBusy("Updating your images…");
                                setError("");
                                try {
                                  const next = assets.filter(
                                    (item) => item.id !== asset.id,
                                  );
                                  await persist(draft, state.origin, next);
                                  if (alive.current) setAssets(next);
                                } catch (err) {
                                  if (alive.current)
                                    setError((err as Error).message);
                                } finally {
                                  if (alive.current) setBusy("");
                                }
                              }}
                            />
                          ))}
                        </div>
                      ) : null}
                  </ReviewGroup>
                </div>
                  <div className="mt-14 space-y-4">
                    {errorAlert}
                    {assetSaveFailed && <Button variant="outline" className="h-10 rounded-md px-5" disabled={!!busy} onClick={() => void saveBrand(true)}>Continue and check images later</Button>}
                    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
                      <p className="text-sm text-muted-foreground">
                        Editing is optional. You can update your brand kit later.
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button variant="ghost" className={quiet} disabled={!!busy && !scanStage} onClick={() => void skipBrand()}>
                          Skip brand setup
                        </Button>
                        <Button
                          form="onboarding-brand-form"
                          variant="brand"
                          type="submit"
                          disabled={!!busy}
                          className={pill}
                        >
                          {busy === "Saving your brand kit…" && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />}
                          Continue
                          {busy !== "Saving your brand kit…" && <ArrowRight aria-hidden="true" className="ml-2 h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {step === 1 && (
              // Only seen while setup moves past the old connect step, or if
              // that move failed.
              <div className="max-w-2xl">
                {intro}
                {errorAlert && <div className="mt-6">{errorAlert}</div>}
                {error && (
                  <Button variant="brand" className={cn(pill, "mt-6")} disabled={!!busy} onClick={() => void leaveSocials(state)}>
                    Try again
                  </Button>
                )}
              </div>
            )}

            {showFirstPost && state && (
              <div className="mx-auto max-w-3xl">
                {intro}
                {errorAlert && <div className="mb-6 flex justify-center">{errorAlert}</div>}
                <FirstPostStep
                  kitId={state.kitId}
                  connectionIds={connectedAccounts.map((account) => account.id)}
                  busy={!!busy}
                  since={state.createdAt}
                  onStarted={() => openWorkspaceWithFirstPost()}
                  onSkip={() => void finish()}
                />
              </div>
            )}
            {postStep && state && (
              // Same width as the composer below, so the heading lines up with it.
              <div className="mx-auto max-w-2xl">
                {intro}
                {errorAlert && <div className="mt-6">{errorAlert}</div>}
                {firstPostStarted ? (
                  // Submitting again would make the posts twice; if opening the
                  // workspace failed, retry only that.
                  <section aria-label="Your first post" className="mt-8 rounded-xl border bg-card p-6">
                    <p className="font-medium">Your first posts are on the way.</p>
                    <p className="mt-1 text-sm text-muted-foreground">They’ll be ready in Drafts in a minute or two.</p>
                    <Button type="button" className="mt-4" disabled={!!busy} onClick={() => void finish(true, false, "/created")}>
                      Open Drafts
                    </Button>
                  </section>
                ) : (
                  <>
                    <section aria-label="Your first post" className="mt-8">
                      <GeneratePage
                        embedded
                        inline
                        initialIntent={firstPostIntent() || ""}
                        sourceHeading="Made from"
                        submitLabel="Make my first post"
                        postsPerSource={5}
                        showManageBrandKits={false}
                        initialBrandKitId={state.brandSaved ? state.kitId : undefined}
                        onSubmitted={() => {
                          setFirstPostStarted(true);
                          void finish(true, false, "/created");
                        }}
                      />
                    </section>
                    <div className="mt-6">
                      <Button type="button" variant="ghost" className={quiet} disabled={!!busy} onClick={() => void finish(true, false, "/generate")}>
                        Skip and go to workspace
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
            {step === 2 && !showFirstPost && state?.step === "engine" && (() => {
              const sourceFormVisible = (!state.engineReady || addingSource) && !busy &&
                (state.origin === "manual" || addingSource || !!error);
              // Automatic discovery ran and found nothing usable on their site.
              const discoveryFailed = sourceFormVisible && !!error && !state.engineReady;
              return (
              <div className="mx-auto flex max-w-lg flex-col items-center gap-8 py-2 text-center sm:py-6">
                {intro}
                <div
                  role="status"
                  aria-live="polite"
                  className="flex w-full flex-col items-center"
                >
                  {!showIdeasLoading && (
                    <div aria-hidden="true" className="mb-6 grid h-14 w-14 place-items-center rounded-2xl bg-[oklch(var(--brand)/0.1)]">
                      {showIdeasReady ? <Check className="h-7 w-7 text-brand-ink" /> : <Sparkles className="h-7 w-7 text-brand-ink" />}
                    </div>
                  )}
                  <p className="onboarding-display max-w-md text-[2.1rem] sm:text-5xl">
                    {showIdeasReady
                      ? "Your post ideas are ready."
                      : showIdeasLoading
                        ? "Finding your first post ideas."
                        : discoveryFailed
                          ? "We couldn’t find posts on your site yet."
                          : "Let’s find your first post ideas."}
                  </p>
                  <p className="mt-4 max-w-sm text-base leading-relaxed text-muted-foreground sm:text-lg">
                    {ideasReady || showIdeasLoading
                      ? "We’re looking across your website for material worth sharing."
                      : discoveryFailed
                        ? "Point us to your blog or news page, or skip for now and add a source later from your workspace."
                        : "Add your website or blog, and we’ll suggest posts shaped by your brand."}
                  </p>
                </div>
                {showIdeasLoading && (
                  <IdeasScanProgress progress={ideasProgress} />
                )}
                {errorAlert && <div className="flex w-full justify-center text-left">{errorAlert}</div>}
                {sourceFormVisible && (
                    <form
                      className="w-full space-y-4 text-left"
                      onSubmit={(e) => {
                        e.preventDefault();
                        void discover(sourceUrl, sourceOwned);
                      }}
                    >
                      <div className="space-y-2">
                        <Label htmlFor="source-url">
                          {state.engineReady
                            ? "Another website or blog"
                            : "Website, blog, or RSS feed"}
                        </Label>
                        <Input
                          id="source-url"
                          className="h-11"
                          value={sourceUrl}
                          disabled={!!busy}
                          onChange={(e) => setSourceUrl(e.target.value)}
                          placeholder="yourbusiness.com/blog"
                        />
                      </div>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-[oklch(var(--brand))]"
                          checked={sourceOwned}
                          disabled={!!busy}
                          onChange={(e) => setSourceOwned(e.target.checked)}
                        />
                        This is my business’s content
                      </label>
                      <Button
                        variant="brand"
                        type="submit"
                        className={pill}
                        disabled={!!busy || !sourceUrl.trim()}
                      >
                        Find content
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </form>
                  )}
                <div className="flex w-full flex-col items-center gap-4">
                  {ideasLoadingVisible && ideasDelayed && (
                    <SetupNotice
                      tone="info"
                      className="text-left"
                      action={
                        <Button
                          variant="outline"
                          className="h-9 rounded-md bg-card px-4"
                          disabled={busy === "Opening your workspace…"}
                          onClick={() => void finish(true, true)}
                        >
                          Go to workspace
                        </Button>
                      }
                    >
                      <p>
                        This is taking longer than usual.{" "}
                        Post ideas will be in your workspace when ready.
                      </p>
                    </SetupNotice>
                  )}
                  {/* Found ideas open the first-post step on their own. */}
                  {!showIdeasLoading && (sourceFormVisible ? (
                    // Finding content is the way forward; skipping stays available but quiet.
                    <Button variant="ghost" className={quiet} onClick={() => void finish()}>
                      Skip for now
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="brand"
                        size="lg"
                        className={cn(pill, "w-full sm:w-auto")}
                        onClick={() => void finish()}
                      >
                        {state.engineReady
                          ? "See post suggestions"
                          : "Skip for now"}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        You choose what to create and publish.
                      </p>
                    </>
                  ))}
                </div>
              </div>
              );
            })()}
          </div>
        )}
        {resizingLogo && logoUrl && (
          <LogoResizeDialog
            source={logoUrl}
            onClose={() => setResizingLogo(false)}
            onSave={uploadLogo}
          />
        )}
      </main>
    </div>
  );
}

function BrandImageReview({
  asset,
  index,
  disabled,
  onRemove,
}: {
  asset: BrandAsset;
  index: number;
  disabled: boolean;
  onRemove: () => void;
}) {
  // Older thumbnail files were cropped to squares. Review the original so
  // users can see the entire asset, including text near its edges.
  const { url, error: imageError } = useFirebaseUrl(asset.storagePath);
  return (
    <div className="relative flex aspect-square items-center justify-center rounded-xl bg-muted/50 p-2">
      {url ? (
        <img
          loading="lazy"
          src={url}
          alt={asset.description || `Brand image ${index + 1}`}
          width={asset.dimensions?.width}
          height={asset.dimensions?.height}
          className="block h-full w-full object-contain"
        />
      ) : (
        <div
          className="flex min-h-24 items-center justify-center p-4 text-sm text-muted-foreground"
          role="status"
        >
          {imageError ? "Image unavailable" : "Loading image…"}
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="absolute right-1 top-1 h-10 w-10 rounded-full bg-background text-muted-foreground shadow-sm hover:border-destructive/30 hover:bg-background hover:text-destructive"
        aria-label={`Remove brand image ${index + 1}`}
        disabled={disabled}
        onClick={onRemove}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
